import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { secureReadingService } from '../api/secureReadingService';
import type { SessionInitiationDto, SecureChunkDto, SessionValidationDto } from '../api/secureReadingService';
import { deviceFingerprintingService } from '../services/deviceFingerprinting';

/**
 * Secure reading session state
 */
export interface SecureReadingSession {
  sessionToken: string;
  bookId: string;
  expiresAt: Date;
  maxChunksPerMinute: number;
  totalChunks: number;
  isActive: boolean;
}

/**
 * Chunk cache entry
 */
interface ChunkCacheEntry {
  chunk: SecureChunkDto;
  fetchedAt: Date;
}

/**
 * Secure reading context state
 */
interface SecureReadingContextState {
  session: SecureReadingSession | null;
  isInitializing: boolean;
  error: string | null;
  chunkCache: Map<number, ChunkCacheEntry>;
  
  // Methods
  initiateSession: (bookId: string) => Promise<void>;
  getChunk: (chunkIndex: number) => Promise<SecureChunkDto>;
  terminateSession: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const SecureReadingContext = createContext<SecureReadingContextState | null>(null);

/**
 * Hook to access secure reading context
 */
export function useSecureReading() {
  const context = useContext(SecureReadingContext);
  if (!context) {
    throw new Error('useSecureReading must be used within SecureReadingProvider');
  }
  return context;
}

/**
 * Provider for secure DRM-protected reading sessions
 */
export function SecureReadingProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SecureReadingSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chunkCache, setChunkCache] = useState<Map<number, ChunkCacheEntry>>(new Map());
  const [isFingerprintReady, setIsFingerprintReady] = useState(false);

  // Refs for intervals and session guard
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);
  const sessionExpiryWarningShown = useRef(false);
  const sessionInitiatedRef = useRef(false);

  /**
   * Initialize device fingerprinting on mount
   */
  useEffect(() => {
    deviceFingerprintingService.initialize()
      .then(() => {
        setIsFingerprintReady(true);
        console.log('Device fingerprinting initialized');
      })
      .catch((err) => {
        console.error('Failed to initialize device fingerprinting:', err);
        setIsFingerprintReady(true); // Allow fallback to proceed anyway
      });
  }, []);

  /**
   * Initiate a new DRM reading session
   */
  const initiateSession = useCallback(async (bookId: string) => {
    // Prevent duplicate initialization (especially in React StrictMode)
    if (sessionInitiatedRef.current) {
      console.log('Session already initiated, skipping duplicate call');
      return;
    }

    sessionInitiatedRef.current = true;

    try {
      setIsInitializing(true);
      setError(null);

      // Check if offline - skip DRM session for offline reading
      if (!navigator.onLine) {
        console.log('📴 Offline mode detected - skipping DRM session (reading from IndexedDB)');
        setIsInitializing(false);
        return; // Content loaded from IndexedDB doesn't need server session
      }

      // Wait for device fingerprinting to be ready (optional - proceed quickly if not ready)
      if (!isFingerprintReady) {
        console.log('Waiting for device fingerprinting initialization...');
        // Poll until ready (with short timeout - fingerprinting is optional)
        const maxWait = 500; // 500ms - fingerprinting is nice-to-have, not required
        const startTime = Date.now();
        while (!isFingerprintReady && Date.now() - startTime < maxWait) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        if (!isFingerprintReady) {
          console.warn('Device fingerprinting not ready - proceeding without it (session still secure)');
        }
      }

      const sessionData = await secureReadingService.initiateSession(bookId);

      const newSession: SecureReadingSession = {
        sessionToken: sessionData.sessionToken,
        bookId,
        expiresAt: new Date(sessionData.expiresAt),
        maxChunksPerMinute: sessionData.maxChunksPerMinute,
        totalChunks: sessionData.totalChunks,
        isActive: true,
      };

      setSession(newSession);
      startHeartbeat(newSession.sessionToken);

      console.log('DRM session initiated:', {
        bookId,
        expiresAt: newSession.expiresAt,
        totalChunks: newSession.totalChunks,
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'Failed to initiate session';
      setError(errorMessage);
      console.error('Session initiation failed:', err);
      sessionInitiatedRef.current = false; // Reset on error to allow retry
      throw err;
    } finally {
      setIsInitializing(false);
    }
  }, [isFingerprintReady]);

  /**
   * Get chunk with caching (max 10 chunks cached)
   */
  const getChunk = useCallback(async (chunkIndex: number): Promise<SecureChunkDto> => {
    if (!session) {
      throw new Error('No active session. Call initiateSession() first.');
    }

    // Check cache first
    const cached = chunkCache.get(chunkIndex);
    if (cached) {
      console.log(`Chunk ${chunkIndex} retrieved from cache`);
      return cached.chunk;
    }

    // Fetch from server
    try {
      const chunk = await secureReadingService.getChunk(
        session.sessionToken,
        session.bookId,
        chunkIndex
      );

      // Update cache (evict oldest if > 10 chunks)
      setChunkCache((prevCache) => {
        const newCache = new Map(prevCache);
        newCache.set(chunkIndex, {
          chunk,
          fetchedAt: new Date(),
        });

        // Evict oldest if cache exceeds 10 chunks
        if (newCache.size > 10) {
          const oldestKey = Array.from(newCache.entries())
            .sort((a, b) => a[1].fetchedAt.getTime() - b[1].fetchedAt.getTime())[0][0];
          newCache.delete(oldestKey);
          console.log(`Evicted chunk ${oldestKey} from cache`);
        }

        return newCache;
      });

      console.log(`Chunk ${chunkIndex} fetched from server`);
      return chunk;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'Failed to fetch chunk';
      setError(errorMessage);
      console.error(`Failed to fetch chunk ${chunkIndex}:`, err);
      throw err;
    }
  }, [session, chunkCache]);

  /**
   * Terminate active session
   */
  const terminateSession = useCallback(async () => {
    if (!session) return;

    try {
      await secureReadingService.terminateSession(session.sessionToken);
      stopHeartbeat();
      setSession(null);
      setChunkCache(new Map());
      sessionExpiryWarningShown.current = false;
      sessionInitiatedRef.current = false; // Allow new session after termination
      deviceFingerprintingService.clearCache(); // Clear fingerprint cache
      console.log('DRM session terminated');
    } catch (err) {
      console.error('Failed to terminate session:', err);
      // Still clear local state even if server call fails
      stopHeartbeat();
      setSession(null);
      setChunkCache(new Map());
      sessionInitiatedRef.current = false; // Allow new session after termination
      deviceFingerprintingService.clearCache(); // Clear fingerprint cache
    }
  }, [session]);

  /**
   * Refresh session to extend expiration
   */
  const refreshSession = useCallback(async () => {
    if (!session) return;

    try {
      const refreshData = await secureReadingService.refreshSession(session.sessionToken);

      setSession((prev) =>
        prev
          ? {
              ...prev,
              expiresAt: new Date(refreshData.expiresAt),
            }
          : null
      );

      sessionExpiryWarningShown.current = false;
      console.log('Session refreshed. New expiration:', refreshData.expiresAt);
    } catch (err) {
      console.error('Failed to refresh session:', err);
      setError('Session refresh failed. Please reload the book.');
    }
  }, [session]);

  /**
   * Start heartbeat interval (60 seconds)
   */
  const startHeartbeat = useCallback((sessionToken: string) => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    heartbeatInterval.current = setInterval(async () => {
      try {
        const validation = await secureReadingService.validateSession(sessionToken);

        if (!validation.isValid) {
          console.warn('Session no longer valid');
          stopHeartbeat();
          setSession((prev) => (prev ? { ...prev, isActive: false } : null));
          setError('Session expired. Please reload the book.');
          return;
        }

        // Warn if session expires in less than 5 minutes
        if (validation.secondsRemaining < 300 && !sessionExpiryWarningShown.current) {
          console.warn(`Session expires in ${validation.secondsRemaining} seconds`);
          sessionExpiryWarningShown.current = true;
          // TODO: Show UI warning to user
        }

        console.log(`Heartbeat: ${validation.secondsRemaining}s remaining, ${validation.chunksAccessed} chunks accessed`);
      } catch (err) {
        console.error('Heartbeat failed:', err);
        // Don't immediately terminate - allow a few failures
      }
    }, 60000); // Every 60 seconds

    console.log('Heartbeat started');
  }, []);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
      console.log('Heartbeat stopped');
    }
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopHeartbeat();
      if (session) {
        secureReadingService.terminateSession(session.sessionToken).catch((err) => {
          console.error('Failed to terminate session on unmount:', err);
        });
      }
    };
  }, [session, stopHeartbeat]);

  // Memoize context value to prevent unnecessary re-renders of consuming components
  // Only recreate when dependencies actually change
  const value: SecureReadingContextState = useMemo(() => ({
    session,
    isInitializing,
    error,
    chunkCache,
    initiateSession,
    getChunk,
    terminateSession,
    refreshSession,
  }), [session, isInitializing, error, chunkCache, initiateSession, getChunk, terminateSession, refreshSession]);

  return (
    <SecureReadingContext.Provider value={value}>
      {children}
    </SecureReadingContext.Provider>
  );
}
